<template>
  <Modal name="CREATE_UPDATE_SERVICE_PROVIDER_REALESTATE" :title="titleLabel" ref="modal">
    <template #body>
      <h-loading v-if="isServiceProviderLoading" />

      <v-form
        v-show="!isServiceProviderLoading"
        v-model="isValid"
        ref="form"
        lazy-validation
        autocomplete="off"
      >
        <HTextInput
          v-model="name"
          :label="nameLabel"
          :rules="[rules.required]"
          inside-label
        />

        <HTextInput
          v-model="cnpj"
          :label="cnpjLabel"
          :rules="[rules.required, rules.documentCheck]"
          inside-label
        />

        <HTextInput
          v-model="information"
          :label="infoLabel"
          inside-label
        />

        <AddressSearch
          @address="getAddressData"
          :getFocus="false"
          :rules="[rules.required]"
          :selectedCountry="'BR'"
          :initialAddress="address"
          :initialPostalCode="postal_code"
          :readonly="true"
        />

        <HTextInput
          v-model="email"
          :label="emailLabel"
          :rules="[rules.email]"
          inside-label
        />

        <HPhone
          v-model="phone"
          :label="phoneLabel"
          :rules="[rules.phone]"
          inside-label
        />

        <hr class="v-divider theme--light my-6" v-if="!isUpdateMode" />

        <template v-if="!isUpdateMode">
          <HTextInput
            v-model="user_name"
            :label="userNameLabel"
            :rules="[rules.required]"
            inside-label
          />

          <HTextInput
            v-model="user_cpfcnpj"
            :label="userCpfCnpjLabel"
            :rules="[rules.required, rules.documentCheck]"
            inside-label
          />

          <HTextInput
            v-model="user_email"
            :label="userEmailLabel"
            :rules="[rules.required, rules.email]"
            inside-label
          />

          <HPhone
            v-model="user_phone"
            :label="userPhoneLabel"
            :rules="[rules.required, rules.phone]"
            inside-label
          />
        </template>

        <template v-if="isUpdateMode">
          <p class="body-1 mt-2 mb-0 font-weight-bold">{{ $t('customer.create.logo') }}</p>
          <p v-show="realEstateLogoUrl && !pendingLogoFile" class="customerLogoWrapper text-center lighten-4 grey mt-5 pa-5">
            <img ref="currentLogoPreview" width="200" :src="realEstateLogoUrl" :alt="name" />
          </p>
          <p v-show="pendingLogoFile" class="customerLogoWrapper text-center lighten-4 grey mt-5 pa-5">
            <img ref="pendingLogoPreview" width="200" src="#" />
          </p>
          <v-card elevation="0">
            <v-progress-linear v-if="isRealEstateLogoUploading" class="mt-n1" indeterminate rounded absolute />
            <HUpload
              @change="handleRealEstateLogoUpload"
              fileTypes="image/png, image/jpeg, image/jpg"
              small
            />
          </v-card>
        </template>

        <v-checkbox
          v-model="allows_key_pickup"
          :label="keyPickupLabel"
        />

        <HTextInput
          v-model="negative_balance_limit"
          :label="negativeBalanceLabel"
          type="number"
          :rules="[v => parseFloat(v) >= 0 || $t('rules.valueRequired')]"
          inside-label
        />

        <v-alert v-if="error" type="error" dense class="mt-4">
          {{ error }}
        </v-alert>

        <div class="d-flex justify-end gap-1">
          <v-btn @click="closeModal" text>
            {{ cancelLabel }}
          </v-btn>

          <v-btn @click="submit" :loading="isServiceProviderLoading" color="primary">
            {{ actionLabel }}
          </v-btn>
        </div>
      </v-form>
    </template>
  </Modal>
</template>
<script>
import {
  email,
  phone,
  documentCheck,
  required,
} from '@/util/errorHandling'

import Modal from '@/components/app/Modal.vue'
import HTextInput from '@/components/app/HTextInput.vue'
import HPhone from '@/components/app/HPhone.vue'
import AddressSearch from '@/components/app/AddressSearch.vue'
import HUpload from '@/components/lib/HUpload.vue'

import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'CreateUpdateServiceProviderRealEstate',

  components: {
    Modal,
    HTextInput,
    HPhone,
    AddressSearch,
    HUpload,
  },

  props: {
    client: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      name: '',
      cnpj: '',
      information: '',
      address: '',
      postal_code: '',
      email: '',
      phone: '',

      user_name: '',
      user_cpfcnpj: '',
      user_email: '',
      user_phone: '',

      allows_key_pickup: false,
      negative_balance_limit: 0.0,
      hcheck_sync_enabled: true,

      isValid: false,
      error: null,
      addressFromAutocomplete: false,
      pendingLogoFile: null,

      titleLabel: this.$t('hausefulUsers.action.createRealEstate'),
      nameLabel: this.$t('onboarding.register.customerName'),
      cnpjLabel: this.$t('onboarding.register.cpfCnpj'),
      infoLabel: this.$t('dashboard.cardAppointment.additionalData'),
      addressLabel: this.$t('dashboard.cardAppointment.address'),
      postalCodeLabel: this.$t('onboarding.address.form.zipCode'),
      phoneLabel: this.$t('onboarding.register.phone'),
      emailLabel: this.$t('onboarding.register.email'),
      userNameLabel: this.$t('transfer.filter.nameForReceiver'),
      userCpfCnpjLabel: this.$t('dashboard.cardAppointment.documentForUser'),
      userEmailLabel: this.$t('onboarding.register.email'),
      userPhoneLabel: this.$t('onboarding.register.phone'),
      keyPickupLabel: this.$t('customer.create.allowKeyPickup'),
      negativeBalanceLabel: this.$t('customer.create.negativeBalanceLimit'),
      cancelLabel: this.$t('hausefulUsers.action.cancel'),
      actionLabel: this.$t('hausefulUsers.action.create'),

      rules: {
        email,
        phone,
        required,
        documentCheck,
        postalCode: v => /^\d{5}-\d{3}$/.test(v) || this.$t('rules.invalidPostalCode'),
      },
    }
  },

  computed: {
    ...mapGetters(['isServiceProviderLoading', 'isRealEstateLogoUploading']),
    isUpdateMode() {
      return !!this.client
    },
    realEstateLogoUrl() {
      return this.client?.logo_url || null
    },
  },

  watch: {
    client: {
      immediate: true,
      handler(newClient) {
        if (newClient) {
          this.name = newClient.name || ''
          this.cnpj = newClient.cnpj || ''
          this.information = newClient.information || ''
          this.address = newClient.address || ''
          this.postal_code = newClient.postal_code || ''
          this.email = newClient.email || ''
          this.phone = newClient.phone || ''
          this.allows_key_pickup = newClient.allows_key_pickup || false
          this.negative_balance_limit = newClient.negative_balance_limit || 0.0
          this.hcheck_sync_enabled = true
          this.titleLabel = this.$t('hausefulUsers.action.createRealEstate')
          this.actionLabel = this.$t('hausefulUsers.action.save')
          this.error = null
          this.user_name = ''
          this.user_cpfcnpj = ''
          this.user_email = ''
          this.user_phone = ''
        } else {
          this.resetFormdata()
          this.titleLabel = this.$t('hausefulUsers.action.createRealEstate')
          this.actionLabel = this.$t('hausefulUsers.action.createRealEstate')
        }
      }
    }
  },

  mounted() {
    const modal = this.$refs.modal
    this.$watch(() => modal.dialog, (value) => {
      if (!value) {
        this.resetFormdata()
        this.error = null
      }
    })
  },

  methods: {
    ...mapActions(['closeModal', 'createServiceProviderRealEstate', 'updateServiceProviderRealEstate', 'listServiceProviderCustomers', 'uploadRealEstateLogo']),

    resetFormdata() {
      this.name = ''
      this.cnpj = ''
      this.information = ''
      this.address = ''
      this.postal_code = ''
      this.email = ''
      this.phone = ''
      this.user_name = ''
      this.user_cpfcnpj = ''
      this.user_email = ''
      this.user_phone = ''
      this.allows_key_pickup = false
      this.negative_balance_limit = 0.0
      this.hcheck_sync_enabled = true
      this.addressFromAutocomplete = false
      this.pendingLogoFile = null
      this.$refs.form?.resetValidation()
      this.error = null
    },

    getAddressData(data) {
      this.address = data.address || ''
      this.postal_code = data.postalCode || ''
      this.addressFromAutocomplete = true
    },

    handleRealEstateLogoUpload(files) {
      const file = files[0]
      if (!file) return
      this.pendingLogoFile = file

      const preview = this.$refs.pendingLogoPreview
      const reader = new FileReader()
      reader.onload = (event) => { preview.src = event.target.result }
      reader.readAsDataURL(file)

      this.uploadRealEstateLogo({ realEstateId: this.client.id, file })
    },

    async submit() {
      if (!this.$refs.form.validate()) return

      if (!this.addressFromAutocomplete) {
        this.error = this.$t('question.create.typeSelectPlaceholder')
        return
      }

      const payload = {
        id: this.client?.id,
        name: this.name,
        cnpj: this.cnpj.replace(/\D/g, ''),
        information: this.information,
        address: this.address,
        postal_code: this.postal_code,
        phone: this.phone,
        email: this.email,
        language: 'PT_BR',
        currency: 'BRAZILIAN_REAL',
        allows_key_pickup: this.allows_key_pickup,
        negative_balance_limit: parseFloat(this.negative_balance_limit || 0),
        hcheck_sync_enabled: true,
        is_tester: false,
      }

      if (!this.isUpdateMode) {
        payload.user = {
          name: this.user_name,
          cpfcnpj: this.user_cpfcnpj.replace(/\D/g, ''),
          phone: this.user_phone,
          email: this.user_email,
        }
      }

      try {
        if (this.isUpdateMode) {
          await this.updateServiceProviderRealEstate(payload)
        } else {
          await this.createServiceProviderRealEstate(payload)
        }
        await this.listServiceProviderCustomers()
        this.$emit('updated')
        this.closeModal()
      } catch (error) {
        const errorMessage = error?.data?.errors?.length
          ? error.data.errors[0].message
          : error?.data?.message || this.$t('forms.errors.generic')
        this.error = errorMessage
      }
    }
  },
}
</script>
